/*
 * @Author: Yumeng Xue
 * @Date: 2026-02-03
 * @Description: Colormap Selector Component with visual colorbar preview
 * @FilePath: /topo-mess/src/components/ui/ColormapSelector.tsx
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Palette } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ColormapName, COLORMAPS, generateColormapGradient } from '../../core/colormaps';

interface ColormapSelectorProps {
    value: ColormapName;
    onChange: (colormap: ColormapName) => void;
    reversed?: boolean;
    label?: string;
    className?: string;
}

export const ColormapSelector: React.FC<ColormapSelectorProps> = ({
    value,
    onChange,
    reversed = true,
    label = 'Colormap',
    className,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 关闭下拉菜单当点击外部
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedColormap = COLORMAPS.find((c) => c.name === value);
    const gradient = generateColormapGradient(value, 32, reversed);

    return (
        <div className={cn('relative space-y-2', className)} ref={dropdownRef}>
            {label && (
                <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-surface-500" />
                    <span className="text-sm font-medium text-surface-600">{label}</span>
                </div>
            )}

            {/* Current Selection Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-200',
                    'bg-white hover:bg-surface-50',
                    isOpen ? 'border-primary-500 shadow-glow' : 'border-surface-200 hover:border-surface-300'
                )}
            >
                {/* Colorbar Preview */}
                <div
                    className="flex-1 h-6 rounded-md shadow-inner"
                    style={{ background: gradient }}
                />
                {/* Label */}
                <span className="text-sm font-medium text-surface-700 min-w-[80px] text-left">
                    {selectedColormap?.label || value}
                </span>
                {/* Chevron */}
                <ChevronDown
                    className={cn(
                        'w-4 h-4 text-surface-400 transition-transform duration-200',
                        isOpen && 'rotate-180'
                    )}
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="relative z-50 w-full max-h-80 overflow-y-auto mt-2 p-2 bg-white rounded-lg border border-surface-200 shadow-lg animate-slide-down">
                    <div className="grid gap-1">
                        {COLORMAPS.map((colormap) => {
                            const itemGradient = generateColormapGradient(colormap.name, 32, reversed);
                            const isSelected = colormap.name === value;

                            return (
                                <button
                                    type="button"
                                    key={colormap.name}
                                    onClick={() => {
                                        onChange(colormap.name);
                                        setIsOpen(false);
                                    }}
                                    className={cn(
                                        'flex items-center gap-3 p-2.5 rounded-lg transition-all duration-150',
                                        'hover:bg-surface-50',
                                        isSelected && 'bg-primary-50 ring-2 ring-primary-500'
                                    )}
                                >
                                    {/* Colorbar */}
                                    <div
                                        className="flex-1 h-5 rounded-md shadow-sm"
                                        style={{ background: itemGradient }}
                                    />
                                    {/* Label */}
                                    <span
                                        className={cn(
                                            'text-sm font-medium min-w-[80px] text-left',
                                            isSelected ? 'text-primary-700' : 'text-surface-600'
                                        )}
                                    >
                                        {colormap.label}
                                    </span>
                                    {/* Check Icon */}
                                    {isSelected && <Check className="w-4 h-4 text-primary-600" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Current Colorbar Display (always visible) */}
            <div className="space-y-1">
                <div
                    className="w-full h-4 rounded-lg shadow-inner border border-surface-200"
                    style={{ background: gradient }}
                />
                <div className="flex justify-between text-xs text-surface-500">
                    <span>Low</span>
                    <span>High</span>
                </div>
            </div>
        </div>
    );
};

/**
 * 独立的 Colorbar 组件，用于显示当前 colormap
 */
interface ColorbarProps {
    colormap: ColormapName;
    reversed?: boolean;
    height?: number;
    showLabels?: boolean;
    className?: string;
}

export const Colorbar: React.FC<ColorbarProps> = ({
    colormap,
    reversed = true,
    height = 16,
    showLabels = true,
    className,
}) => {
    const gradient = generateColormapGradient(colormap, 64, reversed);

    return (
        <div className={cn('space-y-1', className)}>
            <div
                className="w-full rounded-lg shadow-inner border border-surface-200"
                style={{
                    background: gradient,
                    height: `${height}px`,
                }}
            />
            {showLabels && (
                <div className="flex justify-between text-xs text-surface-500">
                    <span>Low</span>
                    <span>High</span>
                </div>
            )}
        </div>
    );
};
